import { createTheme } from '@mui/material/styles';
import { paletteFor } from './designTokens';

// Only palette entries need a per-mode literal: everything under `components`
// resolves --cc-* custom properties at paint time, so those overrides follow
// the [data-theme] attribute without being rebuilt.
export const createAppTheme = (mode = 'light') => {
  const designColors = paletteFor(mode);

  return createTheme({
  palette: {
    mode,
    primary: {
      // accentStrong, not accent: primary.main carries white text on contained
      // buttons and is itself used as a text color via color="primary". The
      // decorative accent only clears 4.5:1 in neither role.
      main: designColors.accentStrong,
      dark: designColors.accentDark,
      light: designColors.accentSoft,
      contrastText: designColors.onAccent,
    },
    secondary: {
      main: designColors.premium,
      light: designColors.premiumSoft,
      // Pinned rather than left to MUI's automatic light/dark pick: in dark
      // mode every one of these fills is the brighter side of the pair, so the
      // ink has to flip with them.
      contrastText: designColors.onAccent,
    },
    background: {
      default: designColors.background,
      paper: designColors.surface,
    },
    text: {
      primary: designColors.text,
      secondary: designColors.textSoft,
    },
    divider: designColors.border,
    success: {
      // Rendered as text via color="success.main" and as a filled button
      // background, so it needs the AA-capable green rather than the
      // decorative one.
      main: designColors.accentStrong,
      contrastText: designColors.onAccent,
    },
    error: {
      main: designColors.danger,
      contrastText: designColors.onAccent,
    },
  },
  typography: {
    fontFamily: '"Hanken Grotesk", "Segoe UI", sans-serif',
    h1: {
      fontFamily: '"Bricolage Grotesque", "Segoe UI", sans-serif',
      fontWeight: 800,
      letterSpacing: '-0.035em',
    },
    h2: {
      fontFamily: '"Bricolage Grotesque", "Segoe UI", sans-serif',
      fontWeight: 800,
      letterSpacing: '-0.025em',
    },
    h3: {
      fontFamily: '"Bricolage Grotesque", "Segoe UI", sans-serif',
      fontWeight: 700,
      letterSpacing: '-0.02em',
    },
    h4: {
      fontFamily: '"Bricolage Grotesque", "Segoe UI", sans-serif',
      fontWeight: 700,
      letterSpacing: '-0.02em',
    },
    h5: {
      fontFamily: '"Bricolage Grotesque", "Segoe UI", sans-serif',
      fontWeight: 700,
    },
    h6: {
      fontFamily: '"Bricolage Grotesque", "Segoe UI", sans-serif',
      fontWeight: 700,
    },
    button: {
      fontWeight: 700,
      textTransform: 'none',
    },
  },
  shape: {
    borderRadius: 14,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        '*': {
          boxSizing: 'border-box',
        },
        '::selection': {
          background: 'var(--cc-accent-soft)',
          color: 'var(--cc-text)',
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          minHeight: 40,
          borderRadius: 11,
        },
        containedPrimary: {
          boxShadow: 'var(--cc-shadow-soft)',
          '&:hover': {
            boxShadow: 'var(--cc-shadow-raised)',
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          minWidth: 40,
          minHeight: 40,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          border: '1px solid var(--cc-border)',
        },
        elevation1: {
          boxShadow: 'var(--cc-shadow-soft)',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 18,
          backgroundColor: 'var(--cc-surface)',
          color: 'var(--cc-text)',
          boxShadow: 'var(--cc-shadow-dialog)',
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          borderRadius: 14,
          backgroundColor: 'var(--cc-surface)',
          color: 'var(--cc-text)',
          boxShadow: 'var(--cc-shadow-raised)',
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          backgroundColor: 'var(--cc-surface)',
          color: 'var(--cc-text)',
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          height: 3,
          borderRadius: '3px 3px 0 0',
          backgroundColor: 'var(--cc-accent)',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          color: 'var(--cc-text-soft)',
          fontWeight: 700,
          textTransform: 'none',
          '&.Mui-selected': {
            color: 'var(--cc-accent-dark)',
          },
        },
      },
    },
    MuiTableCell: {
      defaultProps: {
        size: 'small',
      },
      styleOverrides: {
        root: {
          padding: '9px 10px',
          borderColor: 'var(--cc-border)',
          color: 'var(--cc-text)',
        },
        head: {
          backgroundColor: 'var(--cc-surface-muted)',
          fontWeight: 700,
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundColor: 'var(--cc-surface)',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          borderRadius: 8,
          backgroundColor: 'var(--cc-text)',
          // The background is the text color inverted against the page, so the
          // label has to be the page color — MUI's default white label would
          // vanish on the light tooltip that --cc-text becomes in dark mode.
          color: 'var(--cc-bg)',
          fontSize: 12,
        },
      },
    },
  },
  });
};

// Light theme kept as the default export so modules that import the theme
// directly (outside the provider) keep their existing behaviour.
const theme = createAppTheme('light');

export default theme;
